import JSZip from 'jszip';
import { Product } from '../../types/product';
import { parseCSV } from './csv/parser';
import { processImportedImages } from './images/processor';
import { matchImagesWithProducts } from './images/matcher';

export interface ImportResult {
  success: boolean;
  products: Product[];
  errors: string[];
}

/** Strip any folder prefix from a zip entry path. */
function basename(path: string): string {
  return path.split(/[\\/]/).pop() || path;
}

export async function importFromZip(file: File): Promise<ImportResult> {
  try {
    const zip = await JSZip.loadAsync(file);
    let csvContent = '';
    const imageFiles: File[] = [];

    // Extract files from the zip. The exporter nests everything inside a
    // `yahoo_auction_data/` folder, so we match on the basename only.
    for (const [path, zipEntry] of Object.entries(zip.files)) {
      if (zipEntry.dir) continue;

      const name = basename(path);
      const lower = name.toLowerCase();

      if (lower.endsWith('.csv')) {
        csvContent = await zipEntry.async('string');
      } else if (/\.(jpe?g|png)$/i.test(lower)) {
        const blob = await zipEntry.async('blob');
        const ext = lower.split('.').pop() === 'png' ? 'png' : 'jpeg';
        imageFiles.push(new File([blob], name, { type: `image/${ext}` }));
      }
    }

    if (!csvContent) {
      return {
        success: false,
        products: [],
        errors: ['CSVファイルが見つかりませんでした']
      };
    }

    const { products, productImageFilenames, errors: csvErrors } = await parseCSV(csvContent);
    const { images: processedImages, errors: imageErrors } = await processImportedImages(imageFiles);

    if (imageErrors.length > 0) {
      console.warn('一部の画像の読み込みに失敗しました:', imageErrors);
    }

    // Attach each product's images using the filenames recorded in its own
    // CSV row, rather than assuming a fixed number of images per product.
    products.forEach((product, index) => {
      const filenames = productImageFilenames[index] || [];
      product.images = matchImagesWithProducts(processedImages, filenames).filter(Boolean);
    });

    return {
      success: csvErrors.length === 0 && products.length > 0,
      products,
      errors: csvErrors
    };
  } catch (error) {
    return {
      success: false,
      products: [],
      errors: [(error as Error).message || 'ZIPファイルの読み込みに失敗しました']
    };
  }
}
